import {SummaryData} from '.';
import {
   buildDummySummaryBySection,
   buildManualSummaryPeriodBySection,
   buildManualVendorPayloadBySection,
   normalizeManualVendorBySection,
} from './expense-data-section-strategy';

const MANUAL_VENDOR_DEFAULTS = Object.freeze({
   name: null,
   description: null,
   contractType: null,
   paymentType: null,
   contractOptOut: null,
   companyIpRights: null,
   economicRisk: null,
});

/**
 * Normalizes manual vendor payload shape from API responses.
 * Section-specific rules are delegated to the section strategy module.
 * @param {string} sectionName
 * @param {Object|null|undefined} vendor
 * @returns {Object|null}
 */
export const normalizeManualVendor = (sectionName, vendor) => {
   return normalizeManualVendorBySection({
      sectionName,
      vendor,
      manualVendorDefaults: MANUAL_VENDOR_DEFAULTS,
   });
};

/**
 * Builds the API payload used for creating/updating manual vendors.
 * Section-specific field mapping is delegated to the section strategy module.
 * @param {{sectionName: string, payload: Object, getters: Object, rootGetters: Object}} params
 * @returns {Object}
 */
export const manualVendorPayload = ({sectionName, payload, getters, rootGetters}) => {
   return buildManualVendorPayloadBySection({
      sectionName,
      payload,
      getters,
      rootGetters,
      manualVendorDefaults: MANUAL_VENDOR_DEFAULTS,
   });
};

/**
 * Builds summary create payload entries for each supported study period.
 * The section strategy defines whether each row is vendor-based or contractor-based.
 * @param {{
 *   sectionName: string,
 *   companyId: number|string,
 *   manualVendor: Object,
 *   activeStudyPeriods: Array<{id: number|string, period: Object}>,
 *   projectsSupportedPeriodIds: Array<number|string>,
 *   periodIds: Array<number|string>|null
 * }} params
 * @returns {Array<Object>}
 */
export const buildManualSummaryPeriodsPayload = ({
   sectionName,
   companyId,
   manualVendor,
   activeStudyPeriods,
   projectsSupportedPeriodIds,
   periodIds = null,
}) => {
   return activeStudyPeriods
      .filter(({id}) => projectsSupportedPeriodIds.includes(id))
      .filter(({id}) => periodIds === null || periodIds.includes(id))
      .map(({period}) => {
         const basePeriod = {
            companyId,
            period,
            location: manualVendor.location,
            assigneeId: manualVendor.assigneeId ?? null,
            percentage: null,
            projectsData: {},
            activitiesData: {},
            totalCredit: 0,
         };
         return buildManualSummaryPeriodBySection({sectionName, basePeriod, manualVendor});
      });
};

/**
 * Maps raw summary rows into grouped store structures used by the expense modules.
 * @param {Array<Object>} enrichedSummaries
 * @param {Array<{id: number|string, period: {upper: string}}>} studyPeriods
 * @param {Array<number|string>} projectsSupportedPeriodIds
 * @returns {{accountData: Object<string, Object<string, Array<SummaryData>>>, adHocData: Object<string, Array<SummaryData>>}}
 */
export const mapSummariesToVendorEntries = (
   enrichedSummaries,
   studyPeriods,
   projectsSupportedPeriodIds
) => {
   const periodMap = Object.fromEntries(
      studyPeriods.map((studyPeriod) => [studyPeriod.period.upper, studyPeriod])
   );

   return enrichedSummaries.reduce(
      (acc, raw) => {
         const periodUpper = raw?.period?.upper;
         if (!periodUpper) return acc;

         const mappedPeriod = periodMap[periodUpper];
         if (!mappedPeriod?.id) return acc;

         const normalized = {
            ...raw,
            contractorId: raw.contractorId ?? null,
            totalDebit: raw.totalDebit ?? null,
            totalCredit: raw.totalCredit ?? 0,
         };

         normalized.assignable = normalized.contractorId !== null ? raw.assignable ?? false : false;

         const summary = {...normalized, period: mappedPeriod};

         if (summary.netAmount?.value < 0) return acc;

         if (!projectsSupportedPeriodIds.includes(summary.period.id)) return acc;

         const summaryData = new SummaryData(summary);
         summaryData.assignable = summary.assignable ?? false;
         const key = summary.period.id;

         if (summaryData.isAdHoc) {
            (acc.adHocData[key] ??= []).push(summaryData);
         } else {
            const account = summaryData.account;
            (acc.accountData[key] ??= {})[account] ??= [];
            acc.accountData[key][account].push(summaryData);
         }

         return acc;
      },
      {accountData: {}, adHocData: {}}
   );
};

/**
 * Extracts summary result rows from known API response envelope variants.
 * @param {Object|Array<Object>} [summaryData={}]
 * @returns {Array<Object>}
 */
export const extractSummaryResults = (summaryData = {}) => {
   if (Array.isArray(summaryData?.results?.results)) {
      return summaryData.results.results;
   }

   if (Array.isArray(summaryData?.results)) {
      return summaryData.results;
   }

   if (Array.isArray(summaryData)) {
      return summaryData;
   }

   return [];
};

/**
 * Builds an empty/default ad-hoc SummaryData row for a vendor+period pair.
 * Section-specific seed fields (vendor vs contractor) are supplied by strategy.
 * @param {{sectionName: string, period: Object, vendor: Object, projectsData: Object<string, *>, activitiesData?: Object<string, *>}} params
 * @returns {SummaryData}
 */
export const buildDummySummary = ({
   sectionName,
   period,
   vendor,
   projectsData,
   activitiesData = {},
}) => {
   const baseSummary = {
      period,
      vendorId: vendor.id,
      location: vendor.location,
      totalDebit: null,
      totalCredit: 0,
      percentage: null,
      projectsData: {...projectsData},
      activitiesData: {...activitiesData},
      assigneeId: vendor.assigneeId,
   };
   const summary = buildDummySummaryBySection({sectionName, baseSummary, vendor});
   return new SummaryData(summary);
};
